import React, { useEffect, useState } from 'react';
import { forceUpdate } from '../utils/swUpdate';
import { APP_NETWORK_EVENT, APP_PWA_UPDATE_EVENT } from '../utils/runtimeEvents';
import './AppRuntimeStatus.css';

const SUCCESS_HIDE_DELAY = 3500;
const WARNING_HIDE_DELAY = 5000;

const getInitialState = () => ({
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  unstableMessage: '',
  recoveryMessage: '',
  updateAvailable: false
});

const AppRuntimeStatus = () => {
  const [status, setStatus] = useState(getInitialState);

  useEffect(() => {
    let recoveryTimer = null;
    let warningTimer = null;

    const handleOffline = () => {
      if (recoveryTimer) clearTimeout(recoveryTimer);
      setStatus((prev) => ({
        ...prev,
        online: false,
        recoveryMessage: '',
        unstableMessage: ''
      }));
    };

    const handleOnline = () => {
      setStatus((prev) => ({
        ...prev,
        online: true,
        unstableMessage: '',
        recoveryMessage: prev.online ? '' : 'Conexao restabelecida. Atualize os dados criticos se necessario.'
      }));

      if (recoveryTimer) clearTimeout(recoveryTimer);
      recoveryTimer = setTimeout(() => {
        setStatus((prev) => ({
          ...prev,
          recoveryMessage: ''
        }));
      }, SUCCESS_HIDE_DELAY);
    };

    const handleNetworkEvent = (event) => {
      const detail = event.detail || {};

      if (detail.state === 'offline') {
        handleOffline();
        return;
      }

      setStatus((prev) => ({
        ...prev,
        unstableMessage: detail.message || 'Conexao instavel. Tente novamente.'
      }));

      if (warningTimer) clearTimeout(warningTimer);
      warningTimer = setTimeout(() => {
        setStatus((prev) => ({
          ...prev,
          unstableMessage: ''
        }));
      }, WARNING_HIDE_DELAY);
    };

    const handleUpdateAvailable = () => {
      setStatus((prev) => ({
        ...prev,
        updateAvailable: true
      }));
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener(APP_NETWORK_EVENT, handleNetworkEvent);
    window.addEventListener(APP_PWA_UPDATE_EVENT, handleUpdateAvailable);

    return () => {
      if (recoveryTimer) clearTimeout(recoveryTimer);
      if (warningTimer) clearTimeout(warningTimer);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(APP_NETWORK_EVENT, handleNetworkEvent);
      window.removeEventListener(APP_PWA_UPDATE_EVENT, handleUpdateAvailable);
    };
  }, []);

  const isOffline = !status.online;

  return (
    <>
      {isOffline && (
        <div className="runtime-banner runtime-banner-offline" role="status" aria-live="polite">
          <div className="runtime-banner__content">
            <strong>Sem conexao.</strong>
            <span>Voce ainda pode navegar no app ja carregado, mas salvamentos e acoes criticas exigem internet.</span>
          </div>
        </div>
      )}

      {!isOffline && status.recoveryMessage && (
        <div className="runtime-banner runtime-banner-success" role="status" aria-live="polite">
          <div className="runtime-banner__content">
            <strong>Online novamente.</strong>
            <span>{status.recoveryMessage}</span>
          </div>
        </div>
      )}

      {!isOffline && status.unstableMessage && (
        <div className="runtime-banner runtime-banner-warning" role="status" aria-live="polite">
          <div className="runtime-banner__content">
            <strong>Conexao instavel.</strong>
            <span>{status.unstableMessage}</span>
          </div>
          <button type="button" className="runtime-banner__action" onClick={() => window.location.reload()}>
            Tentar novamente
          </button>
        </div>
      )}

      {status.updateAvailable && (
        <div className="runtime-update-card" role="status" aria-live="polite">
          <div className="runtime-update-card__content">
            <strong>Nova versao disponivel</strong>
            <span>Atualize o app para receber a versao mais recente com seguranca.</span>
          </div>
          <div className="runtime-update-card__actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={forceUpdate}>
              Atualizar
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setStatus((prev) => ({ ...prev, updateAvailable: false }))}
            >
              Depois
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AppRuntimeStatus;
